import { isTextFile } from './fileUtils';

// Model token limits
export const MODEL_TOKEN_LIMITS = {
  'o3': 200000,
  'gpt-5': 400000,
  'gpt-4.1': 1000000,
} as const;

// Rough approximation: 1 token ≈ 4 characters for English text
const CHARS_PER_TOKEN = 4;

// Validation result interface
export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
  totalTokens?: number;
  maxTokens?: number;
}

/**
 * Check if a file is an image based on MIME type
 */
function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Convert text length to estimated token count
 */
function estimateTokenCount(text: string): number {
  return Math.ceil(text.length / CHARS_PER_TOKEN);
}

/**
 * Get file size in tokens for validation purposes
 * - Images: 0 tokens (handled separately)
 * - Text files: convert bytes to tokens
 * - Other files: 0 tokens (validated on backend)
 */
async function getFileTokenCount(file: File): Promise<number> {
  if (isImageFile(file)) {
    return 0; // Images don't count toward token limits
  } else if (isTextFile(file)) {
    // Convert file size from bytes to estimated tokens
    return Math.ceil(file.size / CHARS_PER_TOKEN);
  } else {
    return 0; // Other files validated on backend
  }
}

/**
 * Validate image files according to model-specific rules
 */
function validateImageFiles(files: File[], modelName: string): ValidationResult {
  const imageFiles = files.filter(isImageFile);
  
  if (imageFiles.length === 0) {
    return { isValid: true };
  }
  
  const MAX_IMAGE_SIZE_MB = 20;
  const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
  
  if (modelName === 'o3') {
    // O3: Total size of all images must be < 20MB
    const totalImageSize = imageFiles.reduce((total, file) => total + file.size, 0);
    const totalImageSizeMB = totalImageSize / (1024 * 1024);
    
    if (totalImageSize >= MAX_IMAGE_SIZE_BYTES) {
      return {
        isValid: false,
        errorMessage: `Total image size (${totalImageSizeMB.toFixed(1)}MB) exceeds the 20MB limit for the o3 model. Try using smaller images or fewer images.`
      };
    }
  } else if (modelName === 'gpt-4.1' || modelName === 'gpt-5') {
    // GPT-4.1: Up to 50 images, each < 20MB
    if (imageFiles.length > 50) {
      return {
        isValid: false,
        errorMessage: `Number of images (${imageFiles.length}) exceeds the limit of 50 images for the ${modelName} model.`
      };
    }
    
    for (const file of imageFiles) {
      const fileSizeMB = file.size / (1024 * 1024);
      if (file.size >= MAX_IMAGE_SIZE_BYTES) {
        return {
          isValid: false,
          errorMessage: `Image "${file.name}" (${fileSizeMB.toFixed(1)}MB) exceeds the 20MB limit per image for the ${modelName} model.`
        };
      }
    }
  }
  
  return { isValid: true };
}

/**
 * Validate text files according to model-specific token limits
 */
async function validateTextFiles(files: File[], modelName: string): Promise<ValidationResult> {
  const textFiles = files.filter(isTextFile);
  
  if (textFiles.length === 0) {
    return { isValid: true };
  }
  
  const maxTokens = MODEL_TOKEN_LIMITS[modelName as keyof typeof MODEL_TOKEN_LIMITS] || MODEL_TOKEN_LIMITS['gpt-4.1'];
  let totalTextTokens = 0;
  
  for (const file of textFiles) {
    const fileTokens = await getFileTokenCount(file);
    totalTextTokens += fileTokens;
  }
  
  if (totalTextTokens > maxTokens) {
    const sizeMB = textFiles.reduce((total, file) => total + file.size, 0) / (1024 * 1024);
    return {
      isValid: false,
      errorMessage: `Attached file(s) (${sizeMB.toFixed(1)}MB, ~${totalTextTokens.toLocaleString()} tokens) exceed the ${maxTokens.toLocaleString()} token limit for the ${modelName} model. Try using smaller files or excerpts.`,
      totalTokens: totalTextTokens,
      maxTokens
    };
  }
  
  return { 
    isValid: true,
    totalTokens: totalTextTokens,
    maxTokens
  };
}

/**
 * Calculate total message size including text and file attachments
 */
export async function calculateMessageSize(messageText: string, attachedFiles: File[]): Promise<number> {
  let totalTokens = estimateTokenCount(messageText);
  
  // Add tokens from each attached file
  for (const file of attachedFiles) {
    const fileTokens = await getFileTokenCount(file);
    totalTokens += fileTokens;
  }
  
  return totalTokens;
}

/**
 * Validate complete message (text + files) according to model-specific rules
 */
export async function validateMessage(
  messageText: string, 
  attachedFiles: File[], 
  modelName: string
): Promise<ValidationResult> {
  // First validate images
  const imageValidation = validateImageFiles(attachedFiles, modelName);
  if (!imageValidation.isValid) {
    return imageValidation;
  }
  
  // Then validate text files
  const textValidation = await validateTextFiles(attachedFiles, modelName);
  if (!textValidation.isValid) {
    return textValidation;
  }
  
  // Calculate total tokens including message text and text files
  const totalTokens = await calculateMessageSize(messageText, attachedFiles);
  const maxTokens = MODEL_TOKEN_LIMITS[modelName as keyof typeof MODEL_TOKEN_LIMITS] || MODEL_TOKEN_LIMITS['gpt-4.1'];
  
  // Use 99% of context window as the limit
  const threshold = Math.floor(maxTokens * 0.99);
  
  if (totalTokens > threshold) {
    const percentageOver = Math.round(((totalTokens / threshold) - 1) * 100);
    const modelSpecificAdvice = modelName === 'o3' ? 
      'Try using a non-reasoning expert, smaller images, or excerpts.' : 
      'Try using smaller images or excerpts.';
    
    return {
      isValid: false,
      errorMessage: `Message is ${percentageOver}% over the length limit. ${modelSpecificAdvice}`,
      totalTokens,
      maxTokens: threshold
    };
  }
  
  return {
    isValid: true,
    totalTokens,
    maxTokens: threshold
  };
}

/**
 * Get model name from expert object
 */
export function getModelFromExpert(expert: any): string {
  return expert?.MODEL || expert?.model || 'gpt-4.1';
} 